import { Input } from "@/components/ui/Input";
import {
  SELECT_CLASS,
  textareaClass,
} from "@/features/data-curation/components/SchemaColumnsEditor";
import {
  COLUMN_TYPES,
  MAX_LABEL_LENGTH,
  MAX_NOTE_LENGTH,
  type ColumnDraft,
  type ColumnDraftErrors,
} from "@/features/data-curation/schemaDraft";
import type { CurationValueType } from "@/features/data-curation/types";
import { cn } from "@/utils/cn";

const LABEL_CLASS = "block text-xs font-medium text-zinc-600";

const FieldError = ({ id, message }: { id: string; message: string | undefined }) =>
  message ? (
    <p id={id} className="mt-1 text-xs text-red-700">
      {message}
    </p>
  ) : null;

type ColumnFieldsProps = {
  /** The prefix of the field IDs, from useId. */
  id: string;
  draft: ColumnDraft;
  /** The problems to show. */
  problems: ColumnDraftErrors;
  onChange: (patch: Partial<ColumnDraft>) => void;
  /** The types to offer; a Fast dataset offers fewer. */
  allowedTypes?: CurationValueType[];
};

/** The label, type, and definition of one column, in the add and edit column dialogs. */
export const ColumnFields = ({
  id,
  draft,
  problems,
  onChange,
  allowedTypes = COLUMN_TYPES,
}: ColumnFieldsProps) => {
  const savedType = draft.original?.value_type;
  const types =
    savedType && !allowedTypes.includes(savedType) ? [...allowedTypes, savedType] : allowedTypes;
  return (
    <>
      <div>
        <label htmlFor={`${id}-label`} className={LABEL_CLASS}>
          Label
        </label>
        <Input
          id={`${id}-label`}
          value={draft.label}
          maxLength={MAX_LABEL_LENGTH}
          autoFocus
          error={Boolean(problems.label)}
          aria-describedby={problems.label ? `${id}-label-error` : undefined}
          onChange={(event) => onChange({ label: event.target.value })}
          className="mt-1 text-sm"
        />
        <FieldError id={`${id}-label-error`} message={problems.label} />
      </div>
      <label className={LABEL_CLASS}>
        Type
        <select
          value={draft.valueType}
          onChange={(event) => onChange({ valueType: event.target.value as CurationValueType })}
          className={cn(SELECT_CLASS, "mt-1 block")}
        >
          {types.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </label>
      <label className={LABEL_CLASS}>
        Description
        <textarea
          value={draft.description}
          rows={2}
          maxLength={MAX_NOTE_LENGTH}
          placeholder="What goes in this column, and where the agent finds it"
          onChange={(event) => onChange({ description: event.target.value })}
          className={textareaClass()}
        />
      </label>
      <label className={LABEL_CLASS}>
        Normalization rules
        <textarea
          value={draft.normalization}
          rows={2}
          maxLength={MAX_NOTE_LENGTH}
          placeholder="Optional"
          onChange={(event) => onChange({ normalization: event.target.value })}
          className={textareaClass()}
        />
      </label>
      <div>
        <label className={LABEL_CLASS}>
          Examples, one per line
          <textarea
            value={draft.examplesText}
            rows={2}
            placeholder="Optional"
            aria-invalid={problems.examples ? true : undefined}
            aria-describedby={problems.examples ? `${id}-examples-error` : undefined}
            onChange={(event) => onChange({ examplesText: event.target.value })}
            className={textareaClass(Boolean(problems.examples))}
          />
        </label>
        <FieldError id={`${id}-examples-error`} message={problems.examples} />
      </div>
      {draft.valueType === "categorical" && (
        <div>
          <label className={LABEL_CLASS}>
            Options, one per line
            <textarea
              value={draft.optionsText}
              rows={3}
              placeholder="Optional. A cell must then be one of these values."
              aria-invalid={problems.options ? true : undefined}
              aria-describedby={problems.options ? `${id}-options-error` : undefined}
              onChange={(event) => onChange({ optionsText: event.target.value })}
              className={textareaClass(Boolean(problems.options))}
            />
          </label>
          <FieldError id={`${id}-options-error`} message={problems.options} />
        </div>
      )}
    </>
  );
};
